'use-client';
import * as React from 'react';

import { getDataAttributes } from '@wix/editor-elements-common-utils';
import SiteMembersDialogLayout from '@wix/thunderbolt-elements/src/components/SiteMembersDialogLayout/viewer/SiteMembersDialogLayout';

import type { ISiteMembersInputRef } from '@wix/thunderbolt-elements/src/components/SiteMembersInput/viewer/SiteMembersInput';
import SiteMembersInput from '@wix/thunderbolt-elements/src/components/SiteMembersInput/viewer/SiteMembersInput';
import {
  figureFallbackErrorMessage,
  serverErrorsHandler,
} from '@wix/thunderbolt-elements/src/components/SiteMembersInput/viewer/utils';
import BasicButton from '../../SiteButton/viewer/skinComps/BaseButton/BasicButton.skin';

import type { IVerificationCodeProps } from '../VerificationCodeDialog.types';

import {
  RESEND_INDICATION_TTL_TIMER,
  VerificationCodeDialogTranslationKeys,
  codeInput,
  VerificationCodeDialogTranslationKeys as keys,
  testIds,
} from './constants';
import style from './style/style.scss';
import {
  type StateMachineResponse,
  StateType,
} from '@wix/ambassador-iam-authentication-v1-authentication/types';
import { Loader } from '@wix/design-system';
import classnames from 'clsx';
import type { ITranslationKeys } from '@wix/thunderbolt-elements/src/components/SiteMembersInput/SiteMembersInput.types';

export const waitsFor = (timeout: number) =>
  new Promise(resolve => {
    setTimeout(resolve, timeout);
  });

// A small helper that validates that the verification code is exactly six digits long.
// It returns a translated error message (or undefined/null) which SiteMembersInput
// uses to decide whether the value is valid.
const validateVerificationCode = (
  value: string,
  translations: ITranslationKeys,
): string | null | undefined => {
  // The code must contain exactly 6 numeric characters.
  if (!/^\d{6}$/.test(value)) {
    // Prefer the new translation, fallback to a default English message if missing
    return (
      translations[VerificationCodeDialogTranslationKeys.Code_Invalid] ??
      'Code must be 6 digits long'
    );
  }

  return null;
};

const VerificationCodeDialog: React.FC<IVerificationCodeProps> = props => {
  const {
    id,
    className,
    onCloseDialogCallback,
    onSuccessCallback,
    isCloseable,
    displayMode,
    directionByLanguage,
    email,
    onSubmitCallback,
    onResendVerificationCodeEmail,
    error = '',
    translations,
    isVerificationCodeDialogNewLogic,
  } = props;
  const headlineId = `VerificationCodeHeadline_${id}`;
  const verificationCodeTextInputId = `verificationCodeInput_${id}`;
  const [code, setCode] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [resendSuccessfullyIndication, setResendSuccessfullyIndication] =
    React.useState(false);
  const [resendErrorMessage, setResendErrorMessage] = React.useState('');
  const [resendThrottleTimerValue, setResendThrottleTimerValue] =
    React.useState(30);
  const [codeError, setCodeError] = React.useState(
    figureFallbackErrorMessage(error, translations),
  );
  const reactToResend = React.useCallback(async () => {
    setResendSuccessfullyIndication(true);
    const ticks = new Array(RESEND_INDICATION_TTL_TIMER / 1000).fill('');
    void Promise.all(
      ticks.map(async (_, i) => {
        await waitsFor(i * 1000);
        setResendThrottleTimerValue(ticks.length - i);
        if (ticks.length === i + 1) {
          await waitsFor(1000);
          setResendSuccessfullyIndication(false);
        }
      }),
    );
  }, []);
  const resendVerificationCodeEmail = React.useCallback(async () => {
    try {
      setResendErrorMessage('');
      if (props.isAuthV2Enabled) {
        void reactToResend();
      }
      await onResendVerificationCodeEmail();
    } catch (e) {
      // Failed indication
      // TODO - figure out error message
      setResendErrorMessage('Resend failed');
      setResendSuccessfullyIndication(false);
    }
  }, [onResendVerificationCodeEmail, props.isAuthV2Enabled, reactToResend]);
  const title = translations[keys.title];
  // TODO: get rid of replace once we GA the initial patch and changed the translations
  const emailParagraph = translations[keys.emailParagraph]
    // eslint-disable-next-line no-template-curly-in-string
    ?.replace('${email}', '');
  const submitButtonLabel = translations[keys.submitButton] ?? '';
  const footerParagraph = translations[keys.footerParagraph];
  const footerParagraphLink = translations[keys.footerParagraphLink];
  const codeRef = React.useRef<ISiteMembersInputRef>(null);
  const submitForm = async () => {
    try {
      if (!codeRef.current!.validate(translations)) {
        return;
      }
      setLoading(true);
      if (isVerificationCodeDialogNewLogic) {
        const response: StateMachineResponse = await onSubmitCallback(code);
        if (response?.state === StateType.SUCCESS) {
          setLoading(false);
          onSuccessCallback(response);
        }
        return;
      }
      await onSubmitCallback(code);
    } catch (errorCode: any | string | number) {
      setLoading(false);
      const _errorCode = errorCode ?? 'BAD_CODE';
      const errorMessage = figureFallbackErrorMessage(_errorCode, translations);
      setCodeError(errorMessage);
      const defaultErrorMsg = translations[keys.verificationErr];
      const errorMsg = serverErrorsHandler(errorCode);
      codeRef.current!.setError(
        translations[errorMsg] ?? errorMessage ?? defaultErrorMsg,
      );
    }
  };
  return (
    <SiteMembersDialogLayout
      id={id}
      {...getDataAttributes(props)}
      className={className}
      isCloseable={isCloseable}
      translations={translations}
      onCloseDialogCallback={onCloseDialogCallback}
      headlineId={headlineId}
      displayMode={displayMode}
      dialogPosition="start"
      isCodeVerificationDialog={true}
      role="alertdialog"
      isBuilderComponentModel={props.isBuilderComponentModel}
    >
      <div className={style.memberLoginContent}>
        <form onSubmit={submitForm} aria-labelledby={headlineId}>
          <h2
            className={style.title}
            data-testid={testIds.headline}
            id={headlineId}
          >
            {title}
          </h2>

          <div className={style.paragraph} id={`emailParagraph_${id}`}>
            <span>{emailParagraph}</span>
            <span>{email}</span>
          </div>
          <div className={style.verificationWrapper}>
            <div className={style.enterCode}>
              {/* The htmlFor here must match the id of the input element rendered by TextInput
              inside SiteMembersInput.
              The input id is set via the textInputId prop passed to SiteMembersInput, which is then passed to
              TextInput, which then creates the <input> with an id of `input_${textInputId}`.
              It does not let us control the id of the input directly, so we must explictly match the id generation
              logic.
              */}
              <label htmlFor={`input_${verificationCodeTextInputId}`}>
                {translations[VerificationCodeDialogTranslationKeys.enterCode]}
              </label>
            </div>
            <div className={style.textFieldWrapper}>
              <SiteMembersInput
                maxLength={6}
                id={codeInput}
                textInputId={verificationCodeTextInputId}
                inputType="number"
                data-testid={codeInput}
                value={code}
                label=""
                onValueChanged={setCode}
                ref={codeRef}
                isValid={true}
                autoFocus={true}
                directionByLanguage={directionByLanguage}
                errorMessage={codeError}
                validationFn={validateVerificationCode}
                borderInput
                className={style.inputOverrides}
                ariaDescribedBy={`emailParagraph_${id}`}
              />
            </div>
            <div className={style.ButtonWrapper}>
              {loading ? (
                <div role="status" aria-live="polite" aria-busy="true">
                  <Loader />
                </div>
              ) : (
                <div
                  data-testid="submit"
                  className={classnames({
                    [style.actionButton]: true,
                    [style.errorState]: !!codeError,
                  })}
                >
                  <BasicButton
                    label={submitButtonLabel}
                    id={`okButton_${id}`}
                    isDisabled={false}
                    hasPlatformClickHandler={true}
                    link={undefined}
                    onClick={submitForm}
                    data-testid={testIds.submit}
                  />
                </div>
              )}
            </div>
            <div className={style.paragraph}>
              <span>{footerParagraph}</span>
            </div>
            {!resendSuccessfullyIndication && (
              <div className={style.someInfoTxt}>
                <button
                  className={style.resendButton}
                  data-testid={testIds.resendButton}
                  onClick={resendVerificationCodeEmail}
                  type="button"
                  tabIndex={0}
                >
                  {footerParagraphLink}
                </button>
              </div>
            )}
            <div role="status" aria-atomic="true">
              {resendSuccessfullyIndication && (
                <span className={style.secondaryText}>
                  {
                    translations[
                      VerificationCodeDialogTranslationKeys.resendIndication
                    ]
                  }{' '}
                  {resendThrottleTimerValue.toString()}
                </span>
              )}
              {resendErrorMessage && (
                <div>
                  <span>{resendErrorMessage}</span>
                </div>
              )}
            </div>
          </div>
        </form>
      </div>
    </SiteMembersDialogLayout>
  );
};

export default VerificationCodeDialog;
